import { computed, observable, action } from 'mobx';
import SalonRepository from '../../repositories/SalonRepository';
import Salon from '../../domain/entities/Salon/Salon';
import AnalyticsRepository from '../../repositories/AnalyticsRepository';
import SalonFormStore from '../../domain/entities/SalonFormStore/SalonFormStore';

export const SALONS_PER_PAGE = 5;

interface ISalonComissionVCConfig {
  salonRepository: SalonRepository;
  analyticsRepository: AnalyticsRepository;
}

export interface ISalonCommissionVCData {
  formStore: SalonFormStore;
  isPostalCodeSearchActive: boolean;
  isPrivacyPolicyAccepted: boolean;
  isSearchSalonsLoading: boolean;
  isSelectedSalonChoosenByUser: boolean;
  nearbySalonsData: Salon[];
  searchSalonsData: Salon[];
  searchStringValue: string;
  selectedSalon: Salon;
  showViewMoreSalons: boolean;
  isSalonCommissionToggleChecked: boolean;
}

export default class SalonCommissionViewController {
  private analyticsRepository: AnalyticsRepository;

  private salonRepository: SalonRepository;

  private isOverlayOpen = true;

  @observable private isPostalCodeSearchActive = false;

  @observable private searchSalonsPage = 1;

  @observable private searchSalons: Salon[] = [];

  @observable private hasUserChangedSalon = false;

  @observable private isSearchSalonsLoading = false;

  @observable private searchStringValue: string;

  constructor(config: ISalonComissionVCConfig) {
    this.analyticsRepository = config.analyticsRepository;
    this.salonRepository = config.salonRepository;
  }

  @computed
  get data(): ISalonCommissionVCData {
    const {
      userSalon: {
        isPrivacyPolicyAccepted,
        nearbySalons: nearbySalonsData,
      },
      isSalonCommissionToggleChecked,
    } = this.salonRepository;

    return {
      formStore: this.salonRepository.formStore,
      isPostalCodeSearchActive: this.isPostalCodeSearchActive,
      isPrivacyPolicyAccepted,
      isSalonCommissionToggleChecked,
      isSearchSalonsLoading: this.isSearchSalonsLoading,
      isSelectedSalonChoosenByUser: this.isSelectedSalonChoosenByUser,
      nearbySalonsData,
      searchSalonsData: this.searchSalonsData,
      searchStringValue: this.searchStringValue,
      selectedSalon: this.selectedSalon,
      showViewMoreSalons: this.showViewMoreSalons,
    };
  }

  @computed
  private get searchSalonsData(): Salon[] {
    const { selectedSalonId } = this.salonRepository.userSalon;
    const searchSalonsWithoutSelected = this.searchSalons.filter(
      salon => salon.salonId !== selectedSalonId,
    );
    return searchSalonsWithoutSelected.slice(0, SALONS_PER_PAGE * this.searchSalonsPage);
  }

  @computed
  private get showViewMoreSalons(): boolean {
    if (
      this.searchSalonsData.length
      && SALONS_PER_PAGE * this.searchSalonsPage < this.searchSalons.length
    ) {
      return true;
    }
    return false;
  }

  @computed
  private get selectedSalon(): Salon {
    const { userSalon: { nearbySalons, selectedSalonId } } = this.salonRepository;
    return nearbySalons.find((s: Salon) => s.salonId === selectedSalonId)
    || this.searchSalons.find((s: Salon) => s.salonId === selectedSalonId);
  }

  @computed
  private get isSelectedSalonChoosenByUser(): boolean {
    const { savedUserSalon, userSalon } = this.salonRepository;
    return !!savedUserSalon.selectedSalonId
      || this.hasUserChangedSalon
      || userSalon.nearbySalons.length === 1;
  }

  @action
  viewMoreSalons = (): void => {
    this.searchSalonsPage += 1;
  };

  @action
  toggleEnabled = async (): Promise<void> => {
    const { userSalon,
      isSalonCommissionToggleChecked,
      setToggleChecked,
    } = this.salonRepository;
    if (isSalonCommissionToggleChecked) {
      setToggleChecked(false);
      userSalon.update({
        isPrivacyPolicyAccepted: false,
        selectedSalonId: null,
        stylistName: '',
      });
      this.searchSalons = [];
      this.searchSalonsPage = 1;
      this.searchStringValue = '';
      this.hasUserChangedSalon = false;
    } else {
      setToggleChecked(true);
      if (userSalon.nearbySalons.length) {
        const selectedId = userSalon.nearbySalons[0].salonId;
        userSalon.update({ selectedSalonId: selectedId });
      }
    }
  };

  @action
  searchForSalons = async (searchStringValue: string): Promise<void> => {
    if (searchStringValue) {
      this.isSearchSalonsLoading = true;
      this.searchSalons = [];
      this.searchSalonsPage = 1;
      this.searchStringValue = searchStringValue;
      this.analyticsRepository.salonCommissionSearch(searchStringValue);
      try {
        const salons = await this.salonRepository.getSalonsByZipcode(searchStringValue);
        if (salons && salons.length) {
          this.searchSalons = salons;
        }
      } finally {
        this.isSearchSalonsLoading = false;
      }
    }
  };

  @action
  setSelectedSalon = (salon: Salon, clearNearby?: boolean): void => {
    const { userSalon } = this.salonRepository;
    userSalon.update({ selectedSalonId: salon.salonId });

    if (clearNearby) {
      userSalon.update({ nearbySalons: [salon] });
    }
    this.hasUserChangedSalon = true;
    this.closePostalCodeSearch();
  };

  @action
  updateStylistName = (): void => {
    const { formStore, userSalon, savedUserSalon } = this.salonRepository;
    if (!userSalon.stylistName) {
      formStore.updateStylistName(savedUserSalon.stylistName);
    } else {
      formStore.updateStylistName(userSalon.stylistName);
    }
  };

  @action
  openPostalCodeSearch = (): void => {
    this.isPostalCodeSearchActive = true;
  };

  @action
  closePostalCodeSearch = (): void => {
    this.isPostalCodeSearchActive = false;
    this.searchSalons = [];
  };

  @action
  toggleTermsConditions = (): void => {
    const { userSalon } = this.salonRepository;
    userSalon.update({ isPrivacyPolicyAccepted: !userSalon.isPrivacyPolicyAccepted });
  };

  @action
  toggleSalonCommissionOverlay = (): void => {
    this.isOverlayOpen = !this.isOverlayOpen;
    if (this.isOverlayOpen) {
      this.analyticsRepository.salonCommissionOpenOverlay();
    }
  };
}
